import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { ScopeKey } from '../../domain/access';
import type { DateRange, ScopeFilters } from '../../types/extranet-api.types';

/** Premier jour du mois courant à 00:00 UTC — la période par défaut du header. */
export const startOfMonthISO = () => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
};

const now = () => new Date().toISOString();

export interface GlobalFiltersState {
  /** Valeurs de périmètre par dimension (`billingReference1`, `costCenter`…). */
  scopes: ScopeFilters;
  dateRange: DateRange;
  setScope: (key: ScopeKey, values: string[]) => void;
  setDateRange: (range: DateRange) => void;
  reset: () => void;
}

const initialState = {
  scopes: {} as ScopeFilters,
  dateRange: { from: startOfMonthISO(), to: now() },
};

/** Nombre de valeurs de périmètre actives, toutes dimensions confondues. */
export const countScopeValues = (scopes: ScopeFilters): number =>
  Object.values(scopes).reduce((sum, values) => sum + (values?.length ?? 0), 0);

/**
 * Version 2 : les anciens `entityIds` / `contractIds` / `siteIds` (routes
 * `/v1/extranet/scope/*`, jamais livrées) sont remplacés par les cinq filtres
 * communs du contrat v1.8.4. Un état persisté d'une version antérieure est jeté.
 */
const STORE_VERSION = 2;

export const useFilterStore = create<GlobalFiltersState>()(
  persist(
    set => ({
      ...initialState,
      setScope: (key, values) =>
        set(state => {
          const scopes = { ...state.scopes };
          if (values.length > 0) scopes[key] = values;
          else delete scopes[key];
          return { scopes };
        }),
      setDateRange: range => set({ dateRange: range }),
      reset: () => set(initialState),
    }),
    {
      name: 'extranet:filters',
      version: STORE_VERSION,
      storage: createJSONStorage(() => sessionStorage),
      migrate: (persisted, version) => (version < STORE_VERSION ? initialState : (persisted as GlobalFiltersState)),
    },
  ),
);
